// @givmo/fundraisers-module — every line the Emails tab shows (EMC-WEB1), from the mockup walkthrough
// Jonathan approved (`Email Campaign Plan/scratchpad/2026-10-27-1445-mockup-walkthrough/`, screens 13, 15
// and 17). Each line says where it comes from: DRAWN (on the mockup, character for character), RULED (a
// ruling since) or NEW (the mockup draws no words for it). Screens 15 and 17 await his notes.
//
// SERVED words are the backend's, never copied here: every refusal is the server's own `detail` (save the
// two in `refusals`, whose served words break the module's vocabulary), and the email is the server's own
// render.
//
// ⚠ The module's copy rail reads this file's FULL text, comments included (`copy-law.test.ts`): an email
// that went out says "Went out" (Jonathan, 10-31: "Delivered" is its results' figure, the provider's count),
// and one going out "On its way" (the Messages tab's ruled words; that tab keeps its own).

/** "1,184 people", and "1 person" at one. */
function people(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'person' : 'people'}`;
}

export const EMAILS_COPY = {
  /** DRAWN (13): the tab. */
  tab: 'Emails',
  /** DRAWN (13): the line over the series, and the series' act. */
  sendingAs: 'Sending as',
  newEmail: 'New email',
  /** DRAWN (13): the series' columns. */
  columns: { sends: 'Sends', email: 'Email', audience: 'Audience', status: 'Status', open: 'Open' },
  /** DRAWN (13) for Scheduled and Draft; RULED: On its way (the Messages tab's word) and Went out
   *  (Jonathan, 10-31, beside the results' Delivered figure). NEW: the rest, in the plan's own state words
   *  (§1.1 step 4), and Late (§3.4: a late email says so). */
  status: {
    draft: 'Draft',
    scheduled: 'Scheduled',
    /** NEW (the KCC dev build): the send-time start held it; the reason is said beside it. */
    held: 'Held',
    late: 'Late',
    going: 'On its way',
    delivered: 'Went out',
    /** NEW: it went out when nobody was in its audience (who gets it is read on the day). */
    noOne: 'No one to send to',
    cancelled: 'Cancelled',
    failed: 'Didn’t go out',
  },
  /** NEW: an audience naming more than one thing, after its first. */
  more: (n: number) => `+ ${n} more`,
  /** NEW: a row with no time yet, and one with no subject yet. */
  noTime: 'Not scheduled',
  noSubject: 'Untitled email',
  /** Duplicate (Jonathan, 2026-11-01): the act, in a row's menu and on the email's page. */
  duplicate: 'Duplicate',
  /** NEW. */
  duplicateTitle: 'Duplicate this email',
  /** NEW. */
  duplicateBody: 'Choose the campaign the copy goes in. It starts as an unscheduled draft.',
  /** NEW. */
  duplicateCampaign: 'Campaign',
  /** NEW. */
  duplicateThisCampaign: (name: string) => `${name} (this campaign)`,
  /** NEW. */
  duplicateCancel: 'Cancel',
  /** A row's menu, by its email. */
  rowActions: (subject: string) => `Actions for ${subject}`,
  duplicateFailed: 'The email wasn’t duplicated. Please try again.',
  /** Delete (the KCC hand edit, 2026-11-01): an email not scheduled, from a row's menu and its page. A
   *  scheduled email is cancelled first; one that went out is never deleted. */
  delete: 'Delete',
  deleteTitle: 'Delete this email?',
  deleteBody: 'This email will be deleted and won’t go out. This can’t be undone.',
  deleteConfirm: 'Delete email',
  deleteKeep: 'Keep it',
  deleted: 'Email deleted.',
  deleteFailed: 'The email wasn’t deleted. Please try again.',
  /** Beside an untouched copy's subject, which is the original's, unchanged. */
  copyOf: 'Copy of',
  /** NEW: the series couldn't be read. */
  loadFailed: 'Couldn’t load emails',
  retry: 'Try again',
  /** NEW: the tab's own address while emails don't serve here. */
  unavailableTitle: 'Emails aren’t available',
  unavailableBody: 'This fundraiser can’t send emails here.',
  /** NEW: before the organization's sending domain is ready (D-EMC-5: no fallback sender). */
  domainNotReady: 'You can write emails now and schedule them once your sending domain is ready.',

  // ── An email's page (15, 17) ───────────────────────────────────────────────────────────────────
  /** DRAWN (15): "Email 4 of 9 · Scheduled · Saved". */
  position: (n: number, of: number) => `Email ${n} of ${of}`,
  saved: 'Saved',
  saving: 'Saving…',
  /** DRAWN (15): the panel's three tabs. */
  design: 'Design',
  audience: 'Audience',
  schedule: 'Schedule',
  /** NEW: the preview's frame title for a screen reader, and its two empty states. */
  previewTitle: (subject: string) => `Preview: ${subject}`,
  previewLoading: 'Loading the preview…',
  previewEmpty: 'Nothing to preview yet. Design the email first.',
  openDesigner: 'Design the email',

  /** DRAWN (15): the audience panel. */
  segments: 'Segments',
  everyone: 'Everyone in your contacts',
  campaignDonors: 'People who gave to this campaign',
  tags: 'Tags',
  /** RULED (Jonathan, 2026-10-31): the audience is read when the email sends (plan §2.3), so a segment
   *  or tag count is today's, and an empty one may still reach people by send day. */
  segmentsNote: 'Updated when it sends.',
  countToday: (n: number | null) =>
    n === null || !Number.isFinite(n)
      ? '—'
      : n === 0
        ? 'None yet'
        : `${n.toLocaleString('en-US')} today`,
  thisEmailIs: 'This email is',
  ask: 'An ask',
  /** DRAWN (15), RE-WORDED (the polish-1 user test): one sentence says the skip, with its period; the
   *  summary below no longer says it again. */
  askLine: 'Skips anyone who has given to this campaign by the time it goes out.',
  thank: 'A thank-you',
  thankLine: 'Skips no one, so people who gave get it too.',
  /** DRAWN (15): "About 1,184 people, after unsubscribes are removed". RULED (Jonathan, 2026-10-31):
   *  nobody today is "None yet", with `segmentsNote` under it, since the audience is read when it
   *  goes out and may grow by then; it still schedules. */
  about: (n: number) =>
    n === 0 ? 'None yet' : `About ${people(n)}, after unsubscribes are removed`,
  /** NEW: before anyone is picked, and while the count is read. */
  pickSomeone: 'Pick who gets this email.',
  counting: 'Counting…',
  /** NEW: the server answered no count (its contacts can't be counted now), and the count read failed. */
  notCounted: 'This audience can’t be counted right now.',
  countFailed: 'Couldn’t count this audience.',
  /** NEW (review-24 L4): what picking several means (the server unites them, one copy per address). */
  anyOf: 'Each person gets it once, whichever of these they’re in.',
  /** NEW (the polish-1 user test): how segments and tags combine, said before anything is picked. The
   *  server takes the union of everything the audience names (`org_email_campaign_audience.py`). */
  audienceRule:
    'Pick segments, tags or both. Anyone in at least one of them gets this email, once.',
  /** NEW: a segment deleted since the audience was saved. */
  segmentGone: 'A segment this email used was deleted. Pick its audience again.',
  /** NEW: a segment that uses a condition this version can't read (the server's own words, cut short). */
  segmentUnreadable:
    'A segment this email uses has a condition this version of Givmo can’t read yet.',
  /** NEW: a later version saved this audience; picking replaces it. */
  audienceUnreadable: 'This audience was saved by a newer version of Givmo. Picking replaces it.',
  /** NEW: no CRM to pick from. */
  noCrm: 'Your contacts aren’t available here.',
  /** DRAWN (15): the audience panel's act. */
  continue: 'Continue',

  /** DRAWN (17): the test. */
  sendATest: 'Send a test',
  sendTest: 'Send test',
  /** RULED 2026-10-30 (the copy rail): "Test emailed to you at 10:42 AM." */
  testDone: (time: string) => `Test emailed to you at ${time}.`,
  /** NEW: the test's only address, the person's own sign-in (the server takes no other), shown as words,
   *  never as a greyed field (the polish-1 user test). */
  testTo: 'Goes to your sign-in email.',
  testGoesTo: 'Goes to',
  /** NEW (review-polish A-1): why Send test waits while the sending domain isn't ready (the server
   *  refuses a test from a domain it can't yet send as). */
  testWaitsOnDomain: (domain: string | null) =>
    `Tests can go out once ${domain ?? 'your sending domain'} is ready.`,
  /** NEW (review-polish A-1): two refusals said in the module's words, because the server's own
   *  `detail` for them names its internal vocabulary (`emails-decode.ts` `refusalWords`). */
  refusals: {
    senderNotReady:
      'Tests and schedules can go out once your sending domain is ready. Finish setting it up on the Emails tab.',
    testAddressUnconfirmed:
      'Tests go only to your own sign-in email, once that address is confirmed. Confirm it, then try the test again.',
  },
  /** NEW (the polish-1 user test): the header's "when", as the designer's says it. */
  headerWhen: (day: string, time: string) => `${day} at ${time}`,
  /** DRAWN (17): when it sends. */
  whenItSends: 'When it sends',
  date: 'Date',
  time: 'Time',
  timeZone: 'Time zone',
  /** DRAWN (17): "Tue, Dec 1 at 8:00 AM, New York time". */
  summaryWhen: (day: string, time: string, zone: string) => `${day} at ${time}, ${zone}`,
  /** NEW (the user test): an email that went out, read-only, with the server's own count. */
  wentOut: 'When it went out',
  wentTo: (n: number, from: string | null) => {
    const who = n === 0 ? 'no one' : people(n);
    return from === null ? `To ${who}.` : `To ${who} from ${from}.`;
  },
  /** DRAWN (17): "To about 1,184 people, from appeal@kenchild.org." */
  summaryTo: (n: number | null, from: string | null) =>
    [
      n === null ? null : n === 0 ? 'To no one yet' : `To about ${people(n)}`,
      from === null ? null : `from ${from}`,
    ]
      .filter((part) => part !== null)
      .join(', ') + '.',
  /** NEW (review-24 L5): an ask's count is everyone it names; the skip is decided when it goes out. */
  summaryAskSkip: 'Anyone who has given to this campaign by then is skipped.',
  /** DRAWN (17) for a scheduled email; NEW for the first schedule. */
  scheduleAct: 'Schedule',
  /** NEW (the KCC dev build, 2026-10-31): a draft keeps its date and time before its domain is ready,
   *  so a series can be planned first. The server holds a draft's time as a plan until it is scheduled
   *  (`org_email_campaign_service.update_email`). */
  saveDate: 'Save date',
  dateSaved: 'Date saved. It goes out only once you schedule it.',
  /** NEW (the KCC dev build): beside a greyed Schedule, why it waits. */
  scheduleWaitsOnDomain: (domain: string | null) =>
    `Schedule waits until ${domain ?? 'your sending domain'} is ready. You can save the date now.`,
  /** NEW: beside the time zone, where the organization's own is changed. */
  changeZone: 'Change in Settings',
  orgZone: (zone: string) => `New emails start in ${zone}, your organization’s time zone.`,
  updateSchedule: 'Update schedule',
  /** NEW, RE-WORDED (the polish-1 user test): the way back for a scheduled email (plan §1.1 step 4:
   *  cancel anything not yet going out). It stops the send only: the server keeps the email, its words,
   *  audience and time, as "Cancelled", and it can be changed and scheduled again
   *  (`org_email_campaign_service.cancel_email`). */
  cancelAct: 'Cancel this send',
  cancelTitle: 'Cancel this send?',
  cancelBody: (when: string | null) =>
    `This email won’t go out${
      when === null ? '' : ` on ${when}`
    }. It stays in the series as Cancelled, with its words and audience, and you can schedule it again.`,
  cancelConfirm: 'Cancel this send',
  cancelKeep: 'Keep it scheduled',
  cancelled: 'Send cancelled. The email is still in the series.',
  /** NEW (the polish-1 user test): why "Update schedule" is greyed for a scheduled email. */
  nothingChanged: 'Change the date, time or audience to update the schedule.',
  scheduled: (when: string) => `Scheduled for ${when}.`,
  /** NEW: the lead time's and the year's bounds, said before the server would. */
  tooSoon: 'Pick a time at least 15 minutes from now.',
  tooFar: 'Pick a time within the next year.',
  pickTime: 'Pick a date and time.',
  /** NEW: inside the freeze (five minutes before its time). */
  frozen: 'This email goes out in a few minutes, so it can only be cancelled now.',
  /** NEW (the KCC dev build): past its time, not started, and no hold said (the start hasn't run yet). */
  lateNote:
    'This email is past its time and hasn’t started yet. Givmo keeps trying for up to a day.',
  /**
   * NEW (the KCC dev build): why a held email hasn't gone out and what to do, in the series (short) and on
   * its page (whole). The server tries again until a day after its time, then it fails.
   */
  heldShort: {
    sender_unverified: 'Sending domain isn’t ready',
    campaign_unavailable: 'Campaign isn’t open',
    brand_unreadable: 'Brand images couldn’t be read',
    delivery_off: 'Sending is paused',
  },
  heldNote: {
    sender_unverified:
      'This email is held because your sending domain isn’t working. Finish setting it up on the Emails tab, and it goes out once the domain is ready, up to a day after its time.',
    campaign_unavailable:
      'This email is held because its campaign is closed or no longer available. If the campaign reopens within a day of this email’s time, it goes out; otherwise, cancel this send.',
    brand_unreadable:
      'This email is held because Givmo couldn’t read your brand’s images just now. It tries again by itself, up to a day after its time.',
    delivery_off:
      'This email is held because Givmo has paused sending for now. It goes out when sending resumes, up to a day after its time. You don’t need to do anything.',
  },
  /** NEW (the KCC dev build): the series' short word for why a failed email didn't go out. */
  failedShort: {
    sender_unverified: 'Sending domain wasn’t ready',
    campaign_unavailable: 'Campaign wasn’t open',
    brand_unreadable: 'Brand images couldn’t be read',
    delivery_off: 'Sending was paused',
    too_late: 'Couldn’t start in time',
    crm_unavailable: 'Donors wasn’t turned on',
    body_unreadable: 'Message couldn’t be read',
  },
  /** NEW (the KCC dev build): why a failed email didn't go out, after `failedNote`. */
  failedBecause: {
    sender_unverified: 'Your sending domain wasn’t ready within a day of its time.',
    campaign_unavailable: 'Its campaign was closed or unavailable for a day after its time.',
    brand_unreadable: 'Givmo couldn’t read your brand’s images within a day of its time.',
    delivery_off: 'Givmo’s sending was paused for a day after its time.',
    too_late: 'It couldn’t start within a day of its time.',
    crm_unavailable: 'Donors was turned off for your organization when it was due.',
    body_unreadable: 'Its message couldn’t be read when it was due.',
  },
  /** DRAWN (15): beside the footer the server adds to every email. */
  alwaysIncluded: 'Always included',
  /** NEW: an email whose delivery couldn't run (the server's `failed`): it stays, and can't be changed. */
  failedNote: 'This email didn’t go out. It can’t be changed or scheduled again.',
  /** NEW: an email that went out when nobody was in its audience. */
  noOneNote: 'Nobody was in this audience when it went out.',
  /** NEW: an ask that went out to no one because everyone in its audience had already given. */
  everyoneGaveNote: 'Everyone in this audience had already given when it went out.',
  /** NEW: an email that went out, or is going out, is the record. */
  locked: 'This email has gone out. What it said and who got it can’t change.',
  /** NEW: for a person without the module's Owner tier, who may write but not schedule. */
  ownersSchedule: 'Only the Fundraisers module’s Owners can schedule or cancel emails.',
  /** NEW: the module's pattern for a failure that is no refusal. */
  failed: 'Couldn’t answer this request. Please try again.',

  // ── What each email raised (20) ────────────────────────────────────────────────────────────────
  /** DRAWN (20): the line over the results, "Gifts from these emails: $14,055 (109 gifts)". */
  giftsFromEmails: 'Gifts from these emails:',
  giftCount: (n: number) => `${n.toLocaleString('en-US')} ${n === 1 ? 'gift' : 'gifts'}`,
  /** DRAWN (20): the next email that goes out, "Next: “Meet Morgan” on Fri, Dec 18". */
  next: (subject: string, day: string) => `Next: “${subject}” on ${day}`,
  /** DRAWN (20): the results' columns. NEW: Emailed (the plan's "reached"), so an email whose delivery
   *  reports haven't come in still says how many people it went to. */
  resultsColumns: {
    email: 'Email',
    emailed: 'Emailed',
    delivered: 'Delivered',
    clicks: 'Clicks',
    unsubscribed: 'Unsubscribed',
    gifts: 'Gifts',
    raised: 'Raised',
  },
  /** NEW (review M1): the Clicks label's help. A mail filter that follows a link with GET counts like a
   *  person; nothing at the link can tell the two apart, so the screen says so. */
  clicksHelpLabel: 'About clicks',
  clicksHelp: 'Some email security filters open links on their own, and those count as clicks too.',
  /** NEW (the user test): under a row that went to other than the series' usual audience. */
  toAudience: (words: string) => `To ${words}`,
  toCampaignDonors: 'To people who gave',
  /** DRAWN (20): under an ask's date, "Skipped: 14 already gave". */
  skippedGave: (n: number) => `Skipped: ${n.toLocaleString('en-US')} already gave`,
  /** NEW: what a dash means (only provable numbers: a figure not on record yet is never a 0). */
  resultsNote:
    'A dash means it isn’t known yet. Clicks count each person once, and gifts are the ones made from this email’s links.',
  /** NEW: the emails still to go out, under the results. */
  comingUp: 'Coming up',
  /** NEW: the results on an email's own page. */
  resultsTitle: 'Results',
  /** NEW: the totals couldn't be read (the table still shows each email's figures). */
  totalsUnknown: '—',
} as const;
